// Autoplay til playtest og demo: reservebotten med tre "spillere" i forskellig takt og præcision.
// novice tænker sjældent og tager ofte et næstbedste valg, expert tænker ofte og rammer næsten altid.
//
// Fejlmodellen skalerer med niveau: en fejl er et tilfældigt valg blandt de `spread` bedste
// handlinger (ikke en vilkårlig lovlig handling), og ingen model river ned eller kalder året tidligt
// ved et uheld. Så er en expert altid mindst lige så god som en average.

import { cloneState } from '../sim/state';
import type { Intent, PlayerId, State } from '../sim';
import { adviceIntent, advise } from './coach';
import { rank } from './fallback';

export type AutoplayModel = 'novice' | 'average' | 'expert';

/**
 * `every`: ticks mellem beslutninger. `errorRate`/`spread`: hvor tit og hvor groft den vælger forkert.
 * `idle`: andel af beslutninger den bare lader gå. `threshold`: mindste score før den bruger penge;
 * en expert sparer op til det, der batter, i stedet for at bygge alt der er lidt værd.
 * `foresight`: forbruget ganges med denne faktor, før handlingerne vurderes, så en erfaren spiller
 * bygger til næste års vækst i stedet for kun til i dag. `coach`: følger spillets eget råd først
 * (det samme som hånden og hintet viser), som en spiller der har lært spillet.
 */
export const AUTOPLAY_MODELS: Record<AutoplayModel, { every: number; errorRate: number; spread: number; idle: number; threshold: number; foresight: number; coach: boolean }> = {
  novice: { every: 40, errorRate: 0.3, spread: 5, idle: 0.2, threshold: 5, foresight: 1, coach: false },
  average: { every: 15, errorRate: 0.15, spread: 3, idle: 0.05, threshold: 5, foresight: 1, coach: false },
  expert: { every: 5, errorRate: 0.03, spread: 2, idle: 0, threshold: 40, foresight: 1, coach: true },
};

function ahead(state: State, factor: number): State {
  if (factor === 1) return state;
  const s = cloneState(state);
  for (const n of s.nodes) if (n.city && n.kind === 'city') n.city.demandMw = Math.round(n.city.demandMw * factor);
  s.flow.dirty = true;
  return s;
}

export function isAutoplayModel(s: string | null): s is AutoplayModel {
  return s === 'novice' || s === 'average' || s === 'expert';
}

function rng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** En hjerne til `Game.brain`: returnerer en intent eller null. */
export function autoplayBrain(model: AutoplayModel, seed: number, player: PlayerId = 'p1'): (state: State) => Intent | null {
  const m = AUTOPLAY_MODELS[model];
  const rand = rng(seed ^ 0x5eed);
  const recent: string[] = [];
  return (state) => {
    if (state.tick % m.every !== 0) return null;
    if (rand() < m.idle) return null;
    if (m.coach) {
      const a = advise(state);
      const it = a ? adviceIntent(state, a, player) : null;
      if (it) return it;
    }
    const ranked = rank(ahead(state, m.foresight), player, new Set(recent)).filter(
      (x) => x.cand.kind !== 'wait' && x.cand.kind !== 'demolish' && x.cand.kind !== 'call_year' && x.score > m.threshold,
    );
    if (ranked.length === 0) return null;
    let pick = ranked[0];
    if (rand() < m.errorRate) pick = ranked[Math.floor(rand() * Math.min(m.spread, ranked.length))];
    const c = pick?.cand;
    if (!c) return null;
    recent.push(c.id);
    if (recent.length > 4) recent.shift();
    const i: Intent = { player, clientSeq: 0, kind: c.kind, source: 'bot' };
    if (c.target !== undefined) i.target = c.target;
    if (c.opt !== undefined) i.opt = c.opt;
    return i;
  };
}
